import { Layout } from "providers/Layout";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Input,
  Show
} from "shared/components";
import { LayoutHeader } from "./Components/LayoutHeader";
import { LayoutFooter } from "./Components/LayoutFooter";
import { cx } from "shared/utils";
import { UnitFormProvider } from "./UnitFormProvider";
import { useLocation, useNavigate } from "react-router-dom";
import { CreateUnitForm } from "./Components/CreateUnitForm";
import { Property } from "shared/types";
import { Unit } from "types/landlord/address";
import { useAuthContext } from "providers/Auth";
import { Redirect } from "shared/components/Redirect";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";

interface AddUnitsProps {
  selectedProperty?: Property;
  newUnitName?: string;
  isDrawerContent?: boolean;
  onClose?: () => void;
  onSuccess?: (newUnit: Unit) => void;
}

export const AddUnits = ({
  selectedProperty,
  newUnitName,
  isDrawerContent,
  onClose,
  onSuccess
}: AddUnitsProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { type } = useAuthContext();
  const dictionary = useUnitsPropertiesDictionary();

  const property = isDrawerContent
    ? selectedProperty
    : location.state?.property;

  if (type.isSDK && !type.isBoomScreen) return <Redirect href="/" />;

  return (
    <UnitFormProvider
      propertyId={property?.id ? String(property.id) : undefined}
    >
      <Layout
        sectionClassName={cx("space-y-6", isDrawerContent ? "bg-white" : "")}
        displayAside={!isDrawerContent}
        header={
          <Show if={!isDrawerContent}>
            <LayoutHeader
              propertyName={property.name}
              propertyId={String(property.id)}
            />
          </Show>
        }
        footer={
          <LayoutFooter
            onCancel={() => (isDrawerContent ? onClose?.() : navigate(-1))}
          />
        }
      >
        {isDrawerContent ? (
          <>
            <div className="flex items-center justify-between py-4">
              <h1 className="text-xl font-bold text-content-1000">
                Add {dictionary.units}
              </h1>
              <IconButton
                variant={IconButtonVariant.TEXT_BASE}
                size={IconButtonSize.SM}
                onClick={onClose}
                HugeIcon={Cancel01Icon}
              />
            </div>
            <div className="mb-2 border-b border-alpha-80" />
            <Input
              id="property"
              readOnly
              label={`${dictionary.Property} name`}
              value={property?.name || `No ${dictionary.property} selected`}
            />
            <h5 className="text-content-700 font-medium mb-4 mt-8">
              {dictionary.Units} to add
            </h5>
          </>
        ) : (
          <div className="space-y-2">
            <h1 className="text-xl font-bold">Add {dictionary.unit} details</h1>
            <p className="text-xs text-content-500">
              Add details about the {dictionary.unit}, including its name, rent,
              availability, and location
            </p>
          </div>
        )}

        <CreateUnitForm propertyKind={property.kind} />
      </Layout>
    </UnitFormProvider>
  );
};
